import { readFileSync } from 'node:fs';
import {
  originalPositionFor,
  TraceMap,
  type SourceMapInput,
} from '@jridgewell/trace-mapping';
import type { ErrorPayload, ModuleGraph, Plugin, WebSocketClient } from 'vite';

const packageName = 'runtime-error-plugin';

export default function viteRuntimeErrorOverlayPlugin(options?: {
  filter?: (error: Error) => boolean;
}): Plugin {
  return {
    name: packageName,

    apply(config, env) {
      return env.command === 'serve' && !config.ssr;
    },

    transformIndexHtml() {
      return [
        {
          tag: 'script',
          attrs: { type: 'module' },
          children: CLIENT_SCRIPT,
        },
      ];
    },

    configureServer(server) {
      server.ws.on(MESSAGE_TYPE, (data: unknown, client: WebSocketClient) => {
        const error = Object.assign(new Error(), data);
        if (!error.stack) {
          return;
        }

        if (options?.filter && !options.filter(error)) {
          return;
        }

        const { stack, loc } = rewriteStacktrace(
          error.stack,
          server.moduleGraph,
        );

        const err: ErrorPayload['err'] = {
          name: error.name,
          message: error.message,
          stack,
          loc,
          plugin: packageName,
        };

        if (loc?.file) {
          err.id = loc?.file;
          const source = readFileSync(loc.file, 'utf-8');
          err.frame = generateCodeFrame(source, {
            line: loc.line,
            column: loc.column - 1,
          });
        }

        // Log structured error so the runtime error can be passed to the agentReadWorkflowOutputBuffer
        server.config.logger.error(
          `[RUNTIME_ERROR]${JSON.stringify({
            type: 'runtime-error',
            timestamp: Date.now(),
            name: error.name,
            message: error.message,
            stack,
            loc,
            frame: err.frame,
          })}`,
        );

        client.send({
          type: 'error',
          err,
        });
      });
    },
  };
}

const MESSAGE_TYPE = `${packageName}:error`;
const CLIENT_SCRIPT = `
import { createHotContext } from "/@vite/client";
const hot = createHotContext("/__dummy__${packageName}");

function sendError(error) {
  if (!(error instanceof Error)) {
    error = new Error("(unknown runtime error)");
  }
  const serialized = {
    message: error.message,
    stack: error.stack,
  };
  hot.send("${MESSAGE_TYPE}", serialized);
}

window.addEventListener("error", (evt) => {
  sendError(evt.error);
});

window.addEventListener("unhandledrejection", (evt) => {
  sendError(evt.reason);
});
`;

function cleanStack(stack: string) {
  return stack
    .split(/\n/g)
    .filter((l) => /^\s*at/.test(l))
    .join('\n');
}

function rewriteStacktrace(
  stack: string,
  moduleGraph: ModuleGraph,
): {
  stack: string;
  loc?: Pos & { file: string };
} {
  let loc: (Pos & { file: string }) | undefined = undefined;
  const rewrittenStack = cleanStack(stack)
    .split('\n')
    .map((stackLine) => {
      return stackLine.replace(
        /^ {4}at (?:(\S+?) )?\(?(?:https|http):\/\/[^/]+(\/[^\s?]+).*:(\d+):(\d+)\)?$/,
        (input, varName, url, line, column) => {
          if (!url) {
            return input;
          }

          const moduleEntry = moduleGraph.urlToModuleMap.get(url);
          if (!moduleEntry) {
            return '';
          }

          const rawSourceMap = moduleEntry?.transformResult?.map;

          if (rawSourceMap) {
            const traced = new TraceMap(rawSourceMap as SourceMapInput);
            const pos = originalPositionFor(traced, {
              line: Number(line),
              // stacktrace's column is 1-indexed, but sourcemap's one is 0-indexed
              column: Number(column) - 1,
            });

            if (pos.source && pos.line >= 0 && pos.column >= 0) {
              line = pos.line;
              column = pos.column + 1;
            }
          }

          const trimmedVarName = varName?.trim();
          const sourceFile = moduleEntry.file;
          const source = `${moduleEntry.file}:${line}:${column}`;

          if (sourceFile) {
            loc ??= {
              line: Number(line),
              column: Number(column),
              file: sourceFile,
            };
          }

          if (!trimmedVarName || trimmedVarName === 'eval') {
            return `    at ${source}`;
          } else {
            return `    at ${trimmedVarName} ${source}`;
          }
        },
      );
    })
    .join('\n');

  return {
    stack: rewrittenStack,
    loc,
  };
}

type Pos = {
  /** 1-based */
  line: number;
  /** 0-based */
  column: number;
};

const splitRE = /\r?\n/g;
const range: number = 2;

function posToNumber(source: string, pos: number | Pos): number {
  if (typeof pos === 'number') {
    return pos;
  }

  const lines = source.split(splitRE);
  const { line, column } = pos;
  let start = 0;
  for (let i = 0; i < line - 1 && i < lines.length; i++) {
    start += lines[i].length + 1;
  }

  return start + column;
}

function generateCodeFrame(
  source: string,
  start: number | Pos = 0,
  end?: number | Pos,
): string {
  start = Math.max(posToNumber(source, start), 0);
  end = Math.min(
    end !== undefined ? posToNumber(source, end) : start,
    source.length,
  );
  const lines = source.split(splitRE);
  let count = 0;
  const res: Array<string> = [];
  for (let i = 0; i < lines.length; i++) {
    count += lines[i].length;
    if (count >= start) {
      for (let j = i - range; j <= i + range || end > count; j++) {
        if (j < 0 || j >= lines.length) {
          continue;
        }

        const line = j + 1;
        res.push(
          `${line}${' '.repeat(Math.max(3 - String(line).length, 0))}|  ${
            lines[j]
          }`,
        );
        const lineLength = lines[j].length;
        if (j === i) {
          const pad = Math.max(start - (count - lineLength), 0);
          const length = Math.max(
            1,
            end > count ? lineLength - pad : end - start,
          );
          res.push(`   |  ` + ' '.repeat(pad) + '^'.repeat(length));
        } else if (j > i) {
          if (end > count) {
            const length = Math.max(Math.min(end - count, lineLength), 1);
            res.push(`   |  ` + '^'.repeat(length));
          }

          count += lineLength + 1;
        }
      }

      break;
    }

    count++;
  }

  return res.join('\n');
}
